import { useMemo, useState } from 'react';
import { IconBell, IconCalendar, IconChartBar, IconDots, IconFilter, IconHistory, IconPlus, IconUserCircle, IconSearch, IconSend } from '@tabler/icons-react';
import { useList } from '@hana/data';
import { useT } from '@hana/i18n';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Skeleton, StateView, useToast } from '@hana/ui';
import { financeScreenState, useFinanceAccess } from '../finance/access';
import { norm } from '../finance/view';
import type { ArCustomer } from '../finance/types';
import { arObh, arObhCount, arRows, arSummary, filterAr, sortAr, type ArRow, type ArSortKey } from './ar';
import { DEB_ROWS, debFilter } from './deb';
import { ArDock, useArDock } from './ArDock';
import { DebitTable } from './Debit';
import { MatchQueue } from './Match';
import { ReceiptsTable } from './Receipts';
import s from './ar.module.css';
import './ar.i18n';

// Mint-AR (1536) › tab "Theo khách": bảng tuổi nợ theo khách · hạn mức còn lại · cờ release / giữ DO · nhắc nợ. TIỀN → sau quyền `money`. Chỉ đọc; nút ghi báo toast.

type Tab = 'cus' | 'deb' | 'mat' | 'rcp';
const TABS: readonly (readonly [Tab, number])[] = [['cus', 13], ['deb', 14], ['mat', 4], ['rcp', 4]];
const SORTABLE: Readonly<Record<string, ArSortKey>> = { lim: 'limit', tot: 'total', b4: 'd90', dso: 'dso' };
const nf = (x: number): string => x.toLocaleString('vi-VN', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const nz = (x: number): string => (x ? nf(x) : '—');
const f0 = (x: number): string => Math.round(x).toLocaleString('vi-VN');

export function Ar() {
  const t = useT();
  const toast = useToast();
  const access = useFinanceAccess();
  const state = financeScreenState(access);
  const [tab, setTab] = useState<Tab>('cus');
  const [q, setQ] = useState('');
  const [obh, setObh] = useState(false);
  const [sort, setSort] = useState<{ key: ArSortKey; dir: 'asc' | 'desc' }>({ key: 'over', dir: 'desc' });
  const dock = useArDock();
  const noWrite = () => toast({ msg: t('ar.noWrite') });

  const src = (useList('finArCustomer', { limit: 500 }).data?.items ?? []) as readonly ArCustomer[];
  const all = useMemo(() => arRows(src), [src]);
  const rows = useMemo(() => {
    const n = norm(q.trim());
    const f = filterAr(all).filter((r) => !n || norm(`${r.customer} ${r.sale}`).includes(n)).filter((r) => !obh || arObh(r.customer).open > 0);
    return sortAr(f, sort.key, sort.dir);
  }, [all, q, obh, sort]);
  const debRows = useMemo(() => debFilter(DEB_ROWS, q, norm, obh), [q, obh]);
  const sum = arSummary(rows);
  const ready = state === 'ready';
  const mask = (x: string) => (access.money ? x : '•••••');

  const th = (key: string, label: string, right?: boolean) => {
    const k = SORTABLE[key];
    const on = k && sort.key === k;
    return (
      <th key={key} className={right ? s.r : ''} aria-sort={on ? (sort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}>
        {k ? <button type="button" onClick={() => setSort({ key: k, dir: on && sort.dir === 'desc' ? 'asc' : 'desc' })}>{label} {on ? (sort.dir === 'desc' ? '↓' : '↑') : '↕'}</button> : label}
      </th>
    );
  };

  return (
    <Page kind="table" dock={dock.wide && ready ? <ArDock rows={all} dock={dock} money={access.money} onOpen={() => toast({ msg: t('ar.quickLater') })} /> : undefined}>
      <PageHeader
        title={t('ar.title')}
        findHint={false}
        tabs={
          <div className={s.tabs} role="tablist" aria-label={t('ar.tabs')}>
            {TABS.map(([k, n]) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>{t(`ar.tab.${k}`)}{ready && <span>{n}</span>}</button>
            ))}
            <button type="button" aria-haspopup="menu" aria-expanded="false" onClick={() => toast({ msg: t('ar.groupLater') })}>{t('ar.more')}</button>
          </div>
        }
        actions={
          <>
            <HeaderTool label={t('ar.noti')} icon={IconBell} n={4} onClick={() => toast({ msg: t('ar.notiLater') })} />
            <HeaderTool label={t('ar.dock')} icon={IconChartBar} n={2} onClick={() => dock.toggle('an')} />
            <HeaderTool label={t('ar.other')} icon={IconDots} onClick={noWrite} />
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>{t('ar.record')}</Button>
          </>
        }
      />
      <div className={s.page}>
        <StateView state={state} skeleton={<Skeleton rows={9} />}>
            <>
              <div className={s.bar}>
                <label className={s.search}>
                  <IconSearch size={16} aria-hidden="true" />
                  <input aria-label={t('ar.find')} placeholder={t('ar.findPh')} value={q} onChange={(e) => setQ(e.target.value)} />
                </label>
                <button type="button" className={s.ctl} aria-haspopup="listbox" onClick={() => toast({ msg: t('ar.dateLater') })}><IconCalendar size={16} aria-hidden="true" />{t('ar.asOf')}</button>
                <button type="button" className={s.ctl2} aria-haspopup="menu" onClick={() => toast({ msg: t('ar.filterLater') })}><IconFilter size={16} aria-hidden="true" />{t('ar.filter')}</button>
                <button type="button" className={`${s.ctl2} ${obh ? s.on : ''}`} aria-pressed={obh} title={t('ar.obhTip')} onClick={() => setObh(!obh)}>
                  <IconFilter size={16} aria-hidden="true" />{t('ar.obh')}<span>{arObhCount(all)}</span>
                </button>
                {tab === 'cus' && (
                  <div className={s.tots}>
                    <span>{t('ar.receivable')} <b>{mask(f0(sum.total))}</b></span><span>{t('ar.overdue')} <b className={s.err}>{mask(f0(sum.over))}</b></span>
                  </div>
                )}
              </div>
              {tab === 'mat' ? (
                <MatchQueue money={access.money} />
              ) : tab === 'rcp' ? (
                <ReceiptsTable money={access.money} q={q} />
              ) : tab === 'deb' ? (
                debRows.length === 0 ? <StateView state="empty" title={t('ar.emptyDeb')} desc={t('ar.emptyDesc')} /> : <DebitTable rows={debRows} money={access.money} />
              ) : rows.length === 0 ? (
                <StateView state="empty" title={t('ar.emptyCus')} desc={t('ar.emptyDesc')} />
              ) : (
                <section className={s.card} aria-label={t('ar.byCus')}>
                  <table className={s.tbl}>
                    <thead>
                      <tr>
                        <th className={s.cb} />
                        {th('k', t('ar.col.cus'))}{th('lim', t('ar.col.lim'))}{th('tot', t('ar.col.rec'), true)}{th('nd', t('ar.col.cur'), true)}{th('b1', '1–30', true)}{th('b2', '31–60', true)}{th('b3', '61–90', true)}{th('b4', '> 90', true)}{th('dso', 'DSO', true)}{th('flag', t('ar.col.flag'))}<th />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => <Row key={r.customer} r={r} money={access.money} onRemind={noWrite} onHist={() => toast({ msg: t('ar.histLater') })} onOpen={() => toast({ msg: t('ar.quickLater') })} />)}
                      <tr className={s.sum}>
                        <td /><td><b>{t('ar.total', { n: rows.length })}</b></td><td />
                        <td className={s.r}><b>{mask(nf(sum.total))}</b></td><td className={s.r}>{mask(nf(sum.current))}</td><td className={s.r}>{mask(nf(sum.b1))}</td><td className={s.r}>{mask(nf(sum.b2))}</td><td className={s.r}>{mask(nf(sum.b3))}</td>
                        <td className={`${s.r} ${s.err}`}><b>{mask(nf(sum.b4))}</b></td><td className={s.r}>{sum.dso}</td><td /><td />
                      </tr>
                    </tbody>
                  </table>
                  <div className={s.foot}><span>{t('ar.unit')}</span><span>{t('ar.remSched')}</span></div>
                </section>
              )}
            </>
        </StateView>
      </div>
    </Page>
  );
}

function Row({ r, money, onRemind, onHist, onOpen }: { r: ArRow; money: boolean; onRemind: () => void; onHist: () => void; onOpen: () => void }) {
  const t = useT();
  const m = (x: string) => (money ? x : '•••••');
  const use = Math.min(1, r.total / r.limit);
  const left = r.limit - r.total;
  const bar = left < 0 ? s.bErr : use > 0.85 ? s.bWarn : s.bData;
  const o = arObh(r.customer);
  return (
    <tr>
      <td />
      <td>
        <button type="button" className={s.name} onClick={onOpen}>{r.customer}</button>
        <span className={s.sale}>
          <span title={t('ar.sale', { s: r.sale })} aria-label={t('ar.sale', { s: r.sale })}><i aria-hidden="true">{r.sale.slice(0, 2)}</i>{r.sale}</span>
          <span title={t('ar.collector')} aria-label={t('ar.collector')}><IconUserCircle size={13} aria-hidden="true" />LIENNTK</span>
          <button type="button" className={s.hist} aria-label={t('ar.histOf', { c: r.customer })} title={t('ar.hist')} onClick={onHist}><IconHistory size={13} aria-hidden="true" /></button>
        </span>
      </td>
      <td>
        <div className={s.lim}><span>{money ? f0(r.limit) : t('ar.limMask')}</span><span className={left < 0 ? s.err : ''}>{money ? (left >= 0 ? f0(left) : t('ar.exceed', { n: f0(-left) })) : ''}</span></div>
        <div className={s.track}><div className={bar} style={{ width: `${Math.round(use * 100)}%` }} /></div>
      </td>
      <td className={s.r}>
        <b>{m(nf(r.total))}</b>
        {o.open > 0 && <div className={s.chiho}><span className={o.red ? s.cErr : s.cWarn} title={t('ar.obhTipRow', { n: nf(o.open) })}>{t('ar.obhChip', { n: nf(o.open) })}</span></div>}
      </td>
      <td className={`${s.r} ${s.tx2}`}>{m(nz(r.current))}</td>
      <td className={s.r} style={{ color: r.d1_30 ? 'var(--warn)' : 'var(--lb)' }}>{m(nz(r.d1_30))}</td>
      <td className={s.r} style={{ color: r.d31_60 ? 'var(--warn)' : 'var(--lb)' }}>{m(nz(r.d31_60))}</td>
      <td className={`${s.r} ${r.d61_90 ? s.err : s.lb}`}>{m(nz(r.d61_90))}</td>
      <td className={`${s.r} ${r.d90 ? s.err : s.lb}`} style={{ fontWeight: r.d90 ? 700 : 450 }}>{m(nz(r.d90))}</td>
      <td className={`${s.r} ${r.dso > 45 ? s.err : s.tx2}`}>{r.dso}</td>
      <td>
        {r.flag ? (
          <span className={`${s.flag} ${r.block ? s.fErr : s.fWarn}`} title={r.block ? t('ar.flagBlock') : t('ar.flagWarn')}><i />{r.flag}</span>
        ) : <span className={s.lb}>—</span>}
      </td>
      <td className={s.r}>
        {r.over > 0 && <button type="button" className={s.rem} title={t('ar.remTip')} onClick={onRemind}><IconSend size={16} aria-hidden="true" />{t('ar.remind', { m: r.mark })}</button>}
      </td>
    </tr>
  );
}
